import { Injectable, signal, computed, inject } from '@angular/core';
import { Location } from '@angular/common';
import { from, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { LP_UI_CONFIG } from '../core/tokens';

declare const window: any;
declare const InstallTrigger: any; // for Firefox browser detection

@Injectable({
  providedIn: 'root',
})
export class UiService {
  private readonly cfg = inject(LP_UI_CONFIG);
  private readonly location = inject(Location);

  // ✅ Signals for reactive state
  private isMobileSignal = signal<boolean>(false);
  private isTabletSignal = signal<boolean>(false);
  private isDesktopSignal = signal<boolean>(false);

  private tabIndexSignal = signal<number | null>(null);
  private objectInEditingSignal = signal<number | null>(null);
  private backBtnSignal = signal<boolean>(false);
  private orientationChange = signal<boolean>(false);
  private backBtnTextSignal = signal<string>('');

  // ✅ Navigation callback support
  private navBackCallback: (() => void) | null = null;

  // ✅ Public computed signals
  IsMobile = computed(() => this.isMobileSignal() || this.isTabletSignal());
  IsTablet = computed(() => !this.cfg.disableTabletView && this.isTabletSignal());
  IsDesktop = computed(() => this.isDesktopSignal());

  TabIndex = computed(() => this.tabIndexSignal());
  ObjectInEditing = computed(() => this.objectInEditingSignal());
  BackBtn = computed(() => this.backBtnSignal());
  OrientaionChange = computed(() => this.orientationChange());

  // ✅ Browser detection computed signals
  IsOpera = computed(
    () =>
      (!!window['opr'] && !!window['opr']['addons']) ||
      !!window['opera'] ||
      navigator.userAgent.indexOf(' OPR/') >= 0
  );

  IsFirefox = computed(() => typeof InstallTrigger !== 'undefined');

  IsSafari = computed(
    () =>
      /constructor/i.test(window.HTMLElement?.toString() || '') ||
      (function (p) {
        return p.toString() === '[object SafariRemoteNotification]';
      })(!window['safari'] || window['safari']['pushNotification'])
  );

  IsIE = computed(() => !!(document as any)['documentMode']);

  IsEdge = computed(() => window.navigator.userAgent.indexOf('Edg') > -1);

  IsChrome = computed(() => !!window['chrome'] && !this.IsOpera() && !this.IsEdge());

  IsBlink = computed(() => (this.IsChrome() || this.IsOpera()) && !!window.CSS);

  // ✅ Environment info
  IsPhase2 = this.cfg.phase2;
  environmentDomain = this.cfg.environmentInstanceName;

  constructor() {
    // Initial check
    this.checkViewMode();

    // ✅ Reactive effect for resize
    window.addEventListener('resize', () => this.checkViewMode());
  }

  // ✅ Update methods
  updateTabIndex(idx: number) {
    this.tabIndexSignal.set(idx);
  }

  updateObjectInEditing(id: number) {
    this.objectInEditingSignal.set(id);
  }

  updateBackBtn(backBtn: boolean) {
    this.backBtnSignal.set(backBtn);
  }

  public updateBackBtnText() {
    return this.backBtnTextSignal();
  }

  updateOrientaionChange(orientationChange: boolean) {
    this.orientationChange.set(orientationChange);
  }

  // ✅ Navigation methods
  /**
   * Set a callback function to be invoked when goBack() is called
   * @param callback - Function to execute on back navigation
   */
  public onNavBack(callback: () => void) {
    this.navBackCallback = callback;
  }

  /**
   * Navigate back using the registered callback or browser history
   */
  public goBack() {
    if (this.navBackCallback) {
      this.navBackCallback();
    } else {
      this.location.back();
    }
  }

  // ✅ Back text management
  /**
   * Set the back button text
   * @param text - Text to display on back button
   */
  public setBackText(text: string) {
    this.backBtnTextSignal.set(text);
  }

  // ✅ Debug utility for browser detection
  /**
   * Log browser detection results to console
   */
  // public util_testBrowsers() {
  //   console.log('Detecting browsers by ducktyping:');
  //   console.log('  isFirefox: ' + this.IsFirefox());
  //   console.log('  isChrome: ' + this.IsChrome());
  //   console.log('  isSafari: ' + this.IsSafari());
  //   console.log('  isOpera: ' + this.IsOpera());
  //   console.log('  isIE: ' + this.IsIE());
  //   console.log('  isEdge: ' + this.IsEdge());
  //   console.log('  isBlink: ' + this.IsBlink());
  // }

  // ✅ Camera check remains RxJS-based (async)
  HasCamera() {
    return from(navigator.mediaDevices.enumerateDevices()).pipe(
      switchMap((devices) => {
        const videoDevices = devices.filter((device) => device.kind === 'videoinput');
        return of(videoDevices.length > 0);
      })
    );
  }

  // ✅ View mode detection
  private checkViewMode() {
    const mobile = this.cfg.disableTabletView
      ? document.documentElement.clientHeight > document.documentElement.clientWidth ||
        document.documentElement.clientWidth < 895 ||
        document.documentElement.clientHeight < 600
      : document.documentElement.clientWidth < 767;

    const tablet = this.cfg.disableTabletView
      ? false
      : document.documentElement.clientWidth >= 767 && document.documentElement.clientWidth < 1121;

    const desktop = this.cfg.disableTabletView
      ? !mobile
      : document.documentElement.clientWidth >= 1121;
    this.isMobileSignal.set(mobile);
    this.isTabletSignal.set(tablet);
    this.isDesktopSignal.set(desktop);
    document.body.classList.toggle('mobile', mobile);
    document.body.classList.toggle('tablet', tablet);
    document.body.classList.toggle('desktop', desktop);
    if (window.adobeDataLayer?.[0]) {
      window.adobeDataLayer[0].device['type'] = mobile ? 'mobile' : tablet ? 'tablet' : 'desktop';
    }
  }
}
